import type { ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { auth, type AuthUser } from "../api.js";
import { t } from "../i18n.js";

interface ShellProps {
  user: AuthUser;
  children: ReactNode;
}

function NavLink({ href, children, active }: { href: string; children: ReactNode; active: boolean }) {
  return (
    <Link
      href={href}
      className={`relative px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
        active
          ? "text-brand-900 bg-brand-100/70"
          : "text-slate-600 hover:text-brand-800 hover:bg-surface-1"
      }`}
    >
      {children}
    </Link>
  );
}

export function Shell({ user, children }: ShellProps) {
  const [location] = useLocation();
  const scopeBadge =
    user.role === "owner" || user.role === "super_admin" ? "scope-badge-owner" : "scope-badge-agent";
  const scopeLabel =
    user.role === "owner"
      ? t.roleOwner
      : user.role === "super_admin"
        ? t.roleSuperAdmin
        : user.role === "agent"
          ? t.roleAgent
          : t.roleScanStation;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-surface-1 via-white to-surface-1">
      <header className="bg-white/85 backdrop-blur-lg border-b border-surface-3 sticky top-0 z-30 shadow-depth-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <Link href="/" className="flex items-center gap-2 group">
              <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 text-white font-bold flex items-center justify-center shadow-depth-2 group-hover:shadow-glow-brand transition-shadow">
                D
              </span>
              <span className="font-semibold text-brand-900 tracking-tight">DFA</span>
            </Link>
            <nav className="hidden sm:flex items-center gap-1">
              <NavLink href="/" active={location === "/"}>{t.klienQueue}</NavLink>
              <NavLink href="/audit" active={location === "/audit"}>Audit Log</NavLink>
              {(user.role === "owner" || user.role === "super_admin") && (
                <NavLink href="/pipeline-monitor" active={location === "/pipeline-monitor"}>Monitor</NavLink>
              )}
            </nav>
          </div>
          <div className="flex items-center gap-2.5">
            <span className={scopeBadge}>{scopeLabel}</span>
            <span className="text-sm text-slate-700 hidden sm:inline">{user.name}</span>
            <button
              type="button"
              onClick={() => {
                auth.clear();
                window.location.assign("/login");
              }}
              className="btn-ghost text-xs"
            >
              {t.logout}
            </button>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-6 animate-fade-in">{children}</main>
      <footer className="border-t border-surface-3 bg-white/60 py-4 text-center text-[11px] text-slate-500">
        DCPPI · DFA.web.id · {t.framingDisclaimerShort}
      </footer>
    </div>
  );
}
